import { PayrollListItem } from '@herbjoy/apex-api-models';
import { IQService, IScope } from 'angular';
import { DivisionService, PayStubService } from '../../../core/services';
import { UtilityService } from '../../../core/services/utility.service';
import templateUrl from './pay-stub-container.component.html';

export const PayStubContainerComponent = {
    templateUrl,
    bindings: {
        payrollID: "<payrollId",
        employeeID: "<employeeId",
        isPayStub: "<"
    },
    controllerAs: "vm",
    controller: class PayStubCotainerController {
        payrollID: number;
        employeeID: number;
        isPayStub: boolean;
        /**
         * The pay week displayed on the pay stub.
         */
        payWeek: PayrollListItem;
        /**
         * The pay stub data to be displayed.
         */
        payStub: any;
        /**
         * The collection of available pay weeks
         */
        payWeekList: PayrollListItem[];
        loaded = false;
        reportName: string;
        divisions: any[];
        useDailyRSCommission: any;

        constructor(private $scope: IScope, private $q: IQService, private divisionService: DivisionService,
            private utilityService: UtilityService, private payStubService: PayStubService) {
            'ngInject'
        }

        $onInit() {
            this.reportName = this.isPayStub ? 'Pay Stub Statement' : 'Gross Pay Statement';
            const payrollID = this.payrollID
            this.utilityService.showLoading();

            window.onpopstate = this.onpopstate.bind(this);

            this.$scope.$watch("vm.payWeek", (prev, curr) => {
                if (this.loaded) {
                    this.changePayWeek(this.payWeek)
                        .catch(ex => {
                            // Set back to the original pay week.
                            this.payWeek = prev;
                        });
                }
            });

            return this.loadDivisions()
                .then(divisions => this.loadPayWeeks())
                .then(payWeekList => {
                    this.payWeek = payWeekList.filter(function (payWeek) {
                        return payWeek.PayrollID === payrollID;
                    })[0];
                    return this.payWeek;
                })
                .then(payWeek => {
                    if (!payWeek) {
                        this.payStub = null;
                        return this.payStub;
                    }
                    return this.loadPayStub(this.payWeek);
                })
                .then(payStub => {
                    if (!payStub) {
                        this.payrollID = null;
                        return;
                    }
                    this.payrollID = this.payWeek.PayrollID;
                    const query = {
                        employeeID: payStub.EmployeeID,
                        payrollID: this.payWeek.PayrollID,
                        isPayStub: this.isPayStub
                    };

                    // Set initial state for page navigation.
                    window.history.replaceState(null, this.reportName, "Payroll/GrossPayHistory/" + this.payStub.PayPeriodID
                        + this.utilityService.queryStringify(query));
                })
                .catch(ex => {
                    console.error(ex);
                    alert("Initial load failed: " + this.utilityService.getExceptionMessage(ex));
                })
                .then(() => {
                    // End loading state.
                    this.utilityService.hideLoading();
                    this.loaded = true;
                });

        }

        onPrint() {
            const payWeek = this.payWeek;
            if (!payWeek) {
                return;
            }
            return this.payStubService.exportPayStubPdf(payWeek.PayrollID, this.employeeID, payWeek.IcdCorporationID,
                this.isPayStub);
        }

        /**
         * The url to the commissions grid based on the given payroll.
         */
        commissionsGridUrl(payStub) {
            return 'Payroll/StatementOfEarnings/' + this.payWeek.PayrollID + '?FilteredEmployeeID=' + this.employeeID;
        }

        /**
         * Return a promise that handles a pay week change and loads the associated pay stub.
         * Resolves to the view model's new or current pay stub.
         */
        changePayWeek(payWeek) {
            if (!payWeek || !payWeek.PayrollID) {
                this.payStub = null;
                return this.$q.when();
            }
            this.utilityService.showLoading();
            return this.loadPayStub(payWeek)
                .then(payStub => {
                    const query = {
                        employeeID: payStub.EmployeeID,
                        payrollID: this.payWeek.PayrollID,
                        isPayStub: this.isPayStub
                    };

                    // Push the url state change.
                    window.history.replaceState(null, this.reportName, "Payroll/GrossPayHistory/" + this.payStub.PayPeriodID
                        + this.utilityService.queryStringify(query));

                    this.payrollID = payWeek.PayrollID;
                    return payStub;
                })
                .catch(function (ex) {
                    alert("Loading the pay stub for this week failed: " + ex.statusText);
                })
                .then(() => {
                    // Exit loading state.
                    this.utilityService.hideLoading();
                });
        }

        /**
         * Return a promise that loads the pay weeks and assigns them to the view model's payWeeks property.
         * The promise resolves to the pay week list.
         */
        private loadPayWeeks() {
            return this.payStubService.retrievePayStubs(this.employeeID)
                .then(payStubs => this.sortPayStubs(payStubs))
                .then(payWeeks => {
                    this.payWeekList = payWeeks.map(function (payWeek) {
                        return {
                            PayrollID: payWeek.PayrollID,
                            PayWeekID: payWeek.PayrollID,
                            IcdCorporationID: payWeek.IcdCorporationID,
                            Text: kendo.format("{2}: {0:MM/dd/yyyy} - {1:MM/dd/yyyy}", new Date(payWeek.StartDate), new Date(payWeek.EndDate), payWeek.ClientName),
                            raw: payWeek
                        };
                    });
                    return this.payWeekList;
                })
                .catch(function (ex) {
                    console.error("Retrieving the pay week list failed. " + ex.statusText);
                    throw ex;
                });
        }


        private loadDivisions() {
            return this.divisionService.retrieveDivisions()
                .then(divisions => {
                    this.divisions = divisions
                    return this.divisions;
                });
        }

        /**
         * Return a promise that loads the pay stub for the given week and the view model's employee into the view model.
         * The promise resolves to the pay stub.
         */
        private loadPayStub(payWeek) {
            if (!payWeek) {
                this.payStub = null;
                return this.$q.when();
            }
            return this.payStubService.retrievePayStub(payWeek.PayrollID, this.employeeID, payWeek.IcdCorporationID)
                .then(payStub => {
                    this.payStub = payStub;
                    this.hydrateDivisionRegularCommissions(this.payStub.PayStubEntries);
                    this.payrollID = payStub.PayrollID;
                    this.useDailyRSCommission = payWeek.raw.DivisionCountries.some(dc => {
                        return dc.DivisionID === this.divisionService.divisionIDs.RoadShow;
                    });
                    return payStub;
                })
                .catch(function (ex) {
                    console.error("Retrieving the pay stub failed. " + ex.statusText);
                    throw ex;
                });
        }

        private hydrateDivisionRegularCommissions(entries) {
            entries.forEach(entry => {
                entry.DivisionRegularCommissionList = this.divisions.reduce((list, division) => {
                    const commission = entry.DivisionRegularCommissions[division.Id];
                    if (commission) {
                        division.CommissionAmount = commission;
                        list.push(division);
                    }
                    return list;
                }, []);
            });
        }

        private sortPayStubs(payStubs) {
            // Sort the pay stub by corporation and date.
            return payStubs.sort(function (a, b) {
                return a.ClientName < b.ClientName ? -1 :
                    a.StartDate < b.StartDate ? 1 : -1;
            });
        }

        private onpopstate() {
            if (window.history.state !== null) {
                this.payStub = window.history.state.payStub;
                this.$scope.$apply();
            }
        }
    }
}
